import { useQuery, useQueryClient } from "@tanstack/react-query";
import { boronClient } from "../../api";
import { createError } from "../../errors";
import SkillInterface from "../../interfaces/SkillInterface";

const path = "/api/v1/sections/{section_id}/skills/{skill_hashid}" as const;

const buildKey = (sectionId: string, skillId: string) => [
  path,
  sectionId,
  skillId,
];

export const useFetchSkill = ({
  sectionId,
  skillId,
}: {
  sectionId: string;
  skillId: string;
}) => {
  return useQuery({
    queryKey: buildKey(sectionId, skillId),
    queryFn: async () => {
      const { response, data } = await boronClient.GET(
        "/api/v1/sections/{section_id}/skills/{skill_hashid}",
        {
          params: {
            path: {
              section_id: sectionId,
              skill_hashid: skillId,
            },
          },
        },
      );

      if (response.ok && data) {
        return data.skill;
      }

      throw await createError(response);
    },
  });
};

export const useUpdateSkillCache = ({
  sectionId,
  skillId,
}: {
  sectionId: string;
  skillId: string;
}) => {
  const queryClient = useQueryClient();

  const updateSkillCache = (skill: Partial<SkillInterface>) => {
    queryClient.setQueryData(
      buildKey(sectionId, skillId),
      (old: SkillInterface | undefined) => {
        if (!old) {
          return old;
        }
        return { ...old, ...skill };
      },
    );
  };

  return {
    updateSkillCache,
  };
};
